import { useCallback } from 'react';

export type StudioConstraintRule = {
  id: string;
  when: Record<string, unknown[]>;
  allow?: Record<string, unknown[]>;
  deny?: Record<string, unknown[]>;
};

export type StudioPricingRule = {
  id: string;
  when?: Record<string, unknown>;
  fixed?: number;
  perSecond?: number;
  perImage?: number;
  perMegapixel?: number;
  modifierResolution?: Record<string, number>;
  modifierQuality?: Record<string, number>;
  modifierAudio?: number;
  modifierReferences?: number;
  batchMultiplier?: boolean;
  minimum?: number;
  margin?: number;
  round?: 'ceil' | 'floor' | 'round';
};

export type StudioConfigV2Draft = {
  schemaVersion?: number;
  catalog?: Record<string, unknown>;
  placement?: Record<string, unknown>;
  route?: Record<string, unknown>;
  controls?: unknown[];
  fixedCapabilities?: Record<string, boolean | number | string>;
  constraints?: StudioConstraintRule[];
  planPolicies?: Record<string, unknown>;
  pricing?: { currency?: string; rules?: StudioPricingRule[] };
};

function parseJsonObject(text: string, label: string): Record<string, unknown> {
  const trimmed = text.trim();
  if (!trimmed) return {};
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error(`${label}: ожидается JSON-объект`);
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    throw new Error(
      `${label}: ${error instanceof Error ? error.message : 'некорректный JSON'}`,
    );
  }
}

function JsonObjectField({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: Record<string, unknown>;
  disabled?: boolean;
  onChange: (next: Record<string, unknown>) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="text-ops-muted">{label}</span>
      <textarea
        className="mt-1 min-h-16 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2 font-mono text-xs"
        disabled={disabled}
        defaultValue={JSON.stringify(value, null, 2)}
        onBlur={(event) => {
          try {
            onChange(parseJsonObject(event.target.value, label));
          } catch {
            event.target.value = JSON.stringify(value, null, 2);
          }
        }}
      />
    </label>
  );
}

function ConstraintRuleEditor({
  rule,
  index,
  disabled,
  onChange,
  onRemove,
}: {
  rule: StudioConstraintRule;
  index: number;
  disabled?: boolean;
  onChange: (next: StudioConstraintRule) => void;
  onRemove: () => void;
}) {
  return (
    <div className="space-y-3 rounded-lg border border-ops-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Правило #{index + 1}</span>
        {!disabled ? (
          <button
            type="button"
            className="text-xs text-red-500 hover:underline"
            onClick={onRemove}
          >
            Удалить
          </button>
        ) : null}
      </div>
      <label className="block text-sm">
        <span className="text-ops-muted">id</span>
        <input
          className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2"
          value={rule.id}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, id: e.target.value })}
        />
      </label>
      <JsonObjectField
        label="when (ключ → массив допустимых значений)"
        value={rule.when as Record<string, unknown>}
        disabled={disabled}
        onChange={(when) => onChange({ ...rule, when: when as Record<string, unknown[]> })}
      />
      <JsonObjectField
        label="allow (опционально)"
        value={(rule.allow ?? {}) as Record<string, unknown>}
        disabled={disabled}
        onChange={(allow) => onChange({ ...rule, allow: allow as Record<string, unknown[]> })}
      />
      <JsonObjectField
        label="deny (опционально)"
        value={(rule.deny ?? {}) as Record<string, unknown>}
        disabled={disabled}
        onChange={(deny) => onChange({ ...rule, deny: deny as Record<string, unknown[]> })}
      />
    </div>
  );
}

function PricingRuleEditor({
  rule,
  index,
  disabled,
  onChange,
  onRemove,
}: {
  rule: StudioPricingRule;
  index: number;
  disabled?: boolean;
  onChange: (next: StudioPricingRule) => void;
  onRemove: () => void;
}) {
  const setNumber = (key: keyof StudioPricingRule, raw: string) => {
    onChange({ ...rule, [key]: raw === '' ? undefined : Number(raw) });
  };

  return (
    <div className="space-y-3 rounded-lg border border-ops-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">Pricing #{index + 1}</span>
        {!disabled ? (
          <button
            type="button"
            className="text-xs text-red-500 hover:underline"
            onClick={onRemove}
          >
            Удалить
          </button>
        ) : null}
      </div>
      <label className="block text-sm">
        <span className="text-ops-muted">id</span>
        <input
          className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2"
          value={rule.id}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, id: e.target.value })}
        />
      </label>
      <JsonObjectField
        label="when (опционально, точное совпадение настроек)"
        value={(rule.when ?? {}) as Record<string, unknown>}
        disabled={disabled}
        onChange={(when) => onChange({ ...rule, when })}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            ['fixed', 'fixed (кредиты)'],
            ['perSecond', 'perSecond'],
            ['perImage', 'perImage'],
            ['perMegapixel', 'perMegapixel'],
            ['modifierAudio', 'modifierAudio'],
            ['minimum', 'minimum'],
            ['margin', 'margin'],
          ] as const
        ).map(([key, label]) => (
          <label key={key} className="block text-sm">
            <span className="text-ops-muted">{label}</span>
            <input
              type="number"
              step="any"
              className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2"
              value={rule[key] ?? ''}
              disabled={disabled}
              onChange={(e) => setNumber(key, e.target.value)}
            />
          </label>
        ))}
      </div>
      <JsonObjectField
        label="modifierResolution (resolution → множитель)"
        value={(rule.modifierResolution ?? {}) as Record<string, unknown>}
        disabled={disabled}
        onChange={(modifierResolution) =>
          onChange({ ...rule, modifierResolution: modifierResolution as Record<string, number> })
        }
      />
      <JsonObjectField
        label="modifierQuality (quality → множитель)"
        value={(rule.modifierQuality ?? {}) as Record<string, unknown>}
        disabled={disabled}
        onChange={(modifierQuality) =>
          onChange({ ...rule, modifierQuality: modifierQuality as Record<string, number> })
        }
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={rule.batchMultiplier ?? false}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, batchMultiplier: e.target.checked })}
        />
        <span>batchMultiplier</span>
      </label>
      <label className="block text-sm">
        <span className="text-ops-muted">round</span>
        <select
          className="mt-1 w-full rounded-lg border border-ops-border bg-ops-surface px-3 py-2"
          value={rule.round ?? 'ceil'}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...rule,
              round: e.target.value as StudioPricingRule['round'],
            })
          }
        >
          <option value="ceil">ceil</option>
          <option value="floor">floor</option>
          <option value="round">round</option>
        </select>
      </label>
    </div>
  );
}

type StudioConfigV2EditorProps = {
  config: StudioConfigV2Draft;
  disabled?: boolean;
  onChange: (config: StudioConfigV2Draft) => void;
};

export function StudioConfigV2Editor({ config, disabled, onChange }: StudioConfigV2EditorProps) {
  const constraints = config.constraints ?? [];
  const pricingRules = config.pricing?.rules ?? [];

  const updateConstraints = useCallback(
    (next: StudioConstraintRule[]) => {
      onChange({ ...config, constraints: next });
    },
    [config, onChange],
  );

  const updatePricingRules = useCallback(
    (next: StudioPricingRule[]) => {
      onChange({
        ...config,
        pricing: { ...config.pricing, currency: config.pricing?.currency ?? 'credits', rules: next },
      });
    },
    [config, onChange],
  );

  return (
    <div className="space-y-6">
      <section className="ops-card space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Constraints</h2>
          {!disabled ? (
            <button
              type="button"
              className="rounded-lg border border-ops-border px-3 py-1 text-sm"
              onClick={() =>
                updateConstraints([
                  ...constraints,
                  { id: `constraint-${constraints.length + 1}`, when: {} },
                ])
              }
            >
              + Правило
            </button>
          ) : null}
        </div>
        {constraints.length === 0 ? (
          <p className="text-sm text-ops-muted">Нет constraint-правил</p>
        ) : (
          constraints.map((rule, index) => (
            <ConstraintRuleEditor
              key={`${rule.id}-${index}`}
              rule={rule}
              index={index}
              disabled={disabled}
              onChange={(next) => {
                const copy = [...constraints];
                copy[index] = next;
                updateConstraints(copy);
              }}
              onRemove={() => updateConstraints(constraints.filter((_, i) => i !== index))}
            />
          ))
        )}
      </section>

      <section className="ops-card space-y-4 p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base font-semibold">Pricing rules</h2>
          {!disabled ? (
            <button
              type="button"
              className="rounded-lg border border-ops-border px-3 py-1 text-sm"
              onClick={() =>
                updatePricingRules([
                  ...pricingRules,
                  { id: `pricing-${pricingRules.length + 1}`, round: 'ceil' },
                ])
              }
            >
              + Правило
            </button>
          ) : null}
        </div>
        {pricingRules.length === 0 ? (
          <p className="text-sm text-ops-muted">Нет pricing-правил</p>
        ) : (
          pricingRules.map((rule, index) => (
            <PricingRuleEditor
              key={`${rule.id}-${index}`}
              rule={rule}
              index={index}
              disabled={disabled}
              onChange={(next) => {
                const copy = [...pricingRules];
                copy[index] = next;
                updatePricingRules(copy);
              }}
              onRemove={() => updatePricingRules(pricingRules.filter((_, i) => i !== index))}
            />
          ))
        )}
      </section>

      <section className="ops-card space-y-3 p-4">
        <h2 className="text-base font-semibold">Полный config JSON</h2>
        <pre className="max-h-64 overflow-auto rounded-lg bg-ops-surface p-3 text-xs text-ops-muted">
          {JSON.stringify(config, null, 2)}
        </pre>
      </section>
    </div>
  );
}
